import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Link as LinkIcon, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { createLensShare, exportLens, importLens } from "@/lib/phase3_api";

function copyText(text) {
  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error("copy failed"));
      } catch (e) { reject(e); }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(resolve).catch(fallback);
    } else fallback();
  });
}

export function ShareLensDialog({ vertical, open, onOpenChange }) {
  const [shareUrl, setShareUrl] = useState("");
  const [jsonBlob, setJsonBlob] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!open || !vertical) return;
    setShareUrl(""); setJsonBlob(""); setBusy(true);
    Promise.all([createLensShare(vertical), exportLens(vertical)])
      .then(([share, exp]) => {
        // Use the frontend origin for the public URL (not backend origin).
        const origin = window.location.origin;
        setShareUrl(`${origin}/shared/lens/${share.share_id}`);
        setJsonBlob(JSON.stringify(exp, null, 2));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || e.message || "Share failed"))
      .finally(() => setBusy(false));
  }, [open, vertical]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-2xl" data-testid="share-lens-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Share &ldquo;{vertical}&rdquo;</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 pt-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
              <LinkIcon size={11} /> Public share URL
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="share-url-input"
                value={busy ? "Generating…" : shareUrl}
                readOnly
                className="sharp font-mono text-xs h-9"
              />
              <button
                onClick={() => copyText(shareUrl).then(() => toast.success("URL copied"))}
                disabled={!shareUrl}
                data-testid="copy-share-url"
                className="border border-zinc-950 px-3 h-9 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-950 hover:text-white disabled:opacity-50"
              >
                <Copy size={11} />
              </button>
            </div>
            <div className="mt-1 font-mono text-[10px] text-zinc-500">
              Anyone with this link can view the lens and adopt it into their workspace.
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
              <DownloadSimple size={11} /> JSON export
            </div>
            <Textarea
              data-testid="share-json-blob"
              value={busy ? "Generating…" : jsonBlob}
              readOnly
              rows={10}
              className="sharp font-mono text-xs resize-none"
            />
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => copyText(jsonBlob).then(() => toast.success("JSON copied"))}
                disabled={!jsonBlob}
                data-testid="copy-share-json"
                className="border border-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-100 disabled:opacity-50 flex items-center gap-1"
              >
                <Copy size={11} /> Copy JSON
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([jsonBlob], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `lens-${vertical.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                disabled={!jsonBlob}
                data-testid="download-share-json"
                className="border border-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-100 disabled:opacity-50 flex items-center gap-1"
              >
                <DownloadSimple size={11} /> Download
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImportLensDialog({ open, onOpenChange, onImported }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!raw.trim()) { toast.error("Paste a lens JSON blob"); return; }
    try {
      setBusy(true);
      const parsed = JSON.parse(raw);
      const result = await importLens(parsed);
      toast.success(`Imported "${result.lens.vertical}"`);
      setRaw("");
      onImported && onImported(result);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-2xl" data-testid="import-lens-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Import a lens from JSON</DialogTitle>
        </DialogHeader>
        <div className="pt-2 space-y-3">
          <Textarea
            data-testid="import-json-input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={12}
            placeholder='Paste the JSON blob you received from another workspace…'
            className="sharp font-mono text-xs resize-none"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => onOpenChange(false)}
              className="border border-zinc-300 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-100"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="import-submit"
              className="bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2"
            >
              <UploadSimple size={12} /> {busy ? "Importing…" : "Import"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
